import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Search, ChevronRight, ChevronDown, Code2, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface AdfElement {
  id: string;
  slug: string;
  tag_name: string;
  display_name: string;
  parent_element_id: string | null;
  is_active: boolean;
  display_order: number;
}

interface TreeNode extends AdfElement {
  children: TreeNode[];
}

interface AdfElementPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (slugs: string[]) => void;
  selectedSlugs: string[];
}

function buildTree(elements: AdfElement[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  // Create nodes
  elements.forEach((el) => {
    map.set(el.id, { ...el, children: [] });
  });

  // Build tree
  elements.forEach((el) => {
    const node = map.get(el.id)!;
    if (el.parent_element_id && map.has(el.parent_element_id)) {
      map.get(el.parent_element_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  // Sort by display_order
  const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map((node) => ({ ...node, children: sortNodes(node.children) }));
  };

  return sortNodes(roots);
}

function getAllSlugsInTree(node: TreeNode): string[] {
  const slugs = [node.slug];
  node.children.forEach((child) => {
    slugs.push(...getAllSlugsInTree(child));
  });
  return slugs;
}

function TreeNodeItem({
  node,
  selectedSlugs,
  onToggle,
  expandedIds,
  onToggleExpand,
  searchTerm,
}: {
  node: TreeNode;
  selectedSlugs: Set<string>;
  onToggle: (slug: string) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  searchTerm: string;
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedSlugs.has(node.slug);

  // Check if this node or any children match search
  const matchesSearch = (n: TreeNode): boolean => {
    const term = searchTerm.toLowerCase();
    if (n.display_name.toLowerCase().includes(term) || n.tag_name.toLowerCase().includes(term)) {
      return true;
    }
    return n.children.some(matchesSearch);
  };

  if (searchTerm && !matchesSearch(node)) {
    return null;
  }

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 cursor-pointer group',
          isSelected && 'bg-primary/5'
        )}
      >
        {/* Expand/collapse button */}
        <button
          type="button"
          className={cn(
            'h-5 w-5 flex items-center justify-center text-muted-foreground',
            !hasChildren && 'invisible'
          )}
          onClick={() => onToggleExpand(node.id)}
        >
          {hasChildren &&
            (isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            ))}
        </button>

        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(node.slug)}
          id={`element-${node.id}`}
        />

        {/* Label */}
        <label
          htmlFor={`element-${node.id}`}
          className="flex-1 flex items-center gap-2 text-sm cursor-pointer"
        >
          <Code2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{node.display_name}</span>
          <span className="text-muted-foreground text-xs">&lt;{node.tag_name}&gt;</span>
        </label>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="ml-5 border-l border-border/50 pl-2">
          {node.children.map((child) => (
            <TreeNodeItem
              key={child.id}
              node={child}
              selectedSlugs={selectedSlugs}
              onToggle={onToggle}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdfElementPicker({
  open,
  onOpenChange,
  onSelect,
  selectedSlugs: initialSelectedSlugs,
}: AdfElementPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(
    new Set(initialSelectedSlugs)
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Fetch ADF elements
  const { data: elements = [], isLoading } = useQuery({
    queryKey: ['adf-elements-picker'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_elements')
        .select('id, slug, tag_name, display_name, parent_element_id, is_active, display_order')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return data as AdfElement[];
    },
  });

  // Build tree structure
  const tree = useMemo(() => buildTree(elements), [elements]);

  // Reset selection when dialog opens
  useMemo(() => {
    if (open) {
      setSelectedSlugs(new Set(initialSelectedSlugs));
      // Auto-expand first level
      const firstLevelIds = new Set(tree.map((n) => n.id));
      setExpandedIds(firstLevelIds);
    }
  }, [open, initialSelectedSlugs, tree]);

  const toggleSelection = (slug: string) => {
    const newSelected = new Set(selectedSlugs);
    if (newSelected.has(slug)) {
      newSelected.delete(slug);
    } else {
      newSelected.add(slug);
    }
    setSelectedSlugs(newSelected);
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleConfirm = () => {
    onSelect(Array.from(selectedSlugs));
    onOpenChange(false);
  };

  const handleSelectAll = () => {
    const allSlugs = elements.map((e) => e.slug);
    setSelectedSlugs(new Set(allSlugs));
  };

  const handleDeselectAll = () => {
    setSelectedSlugs(new Set());
  };

  const handleExpandAll = () => {
    setExpandedIds(new Set(elements.map((e) => e.id)));
  };

  const handleCollapseAll = () => {
    setExpandedIds(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="h-5 w-5" />
            Select ADF Elements
          </DialogTitle>
          <DialogDescription>
            Choose which elements to include in this template.
            <Badge variant="secondary" className="ml-2">
              {selectedSlugs.size} selected
            </Badge>
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search elements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Toolbar */}
        <div className="flex gap-2 text-xs">
          <Button variant="ghost" size="sm" onClick={handleSelectAll} className="h-7 text-xs">
            Select All
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDeselectAll} className="h-7 text-xs">
            Deselect All
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={handleExpandAll} className="h-7 text-xs">
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={handleCollapseAll} className="h-7 text-xs">
            Collapse
          </Button>
        </div>

        {/* Tree */}
        <ScrollArea className="flex-1 border rounded-md p-2 min-h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tree.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No elements found</p>
          ) : (
            tree.map((node) => (
              <TreeNodeItem
                key={node.id}
                node={node}
                selectedSlugs={selectedSlugs}
                onToggle={toggleSelection}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                searchTerm={searchTerm}
              />
            ))
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Add Selected ({selectedSlugs.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
